import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import plusViolet from '../../../static/img/add-violet.svg';
import chevronDown from '../../../static/img/angledownwhite20.svg';
import uploadIcon from '../../../static/icons/share-download-icon.svg';
import {
  NewDraftMenu,
  NewDraftMenuIcon,
  NewDraftMenuItem,
  NewDraftMenuLabel,
  SplitButtonChevron,
  SplitButtonContainer,
  SplitButtonDropdown,
  SplitButtonMain,
  SplitButtonWrap,
} from '../styles';

// `open` / `onOpenChange` are optional: pass both to control the menu from the
// outside (the Exams New menu's "New draft" opens it); without them the
// button keeps its own state exactly as before.
export const NewDraftSplitButton = ({
  onSingleDraftClick,
  onBulkUploadClick,
  open: controlledOpen,
  onOpenChange,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? Boolean(controlledOpen) : uncontrolledOpen;
  const setOpen = (next) => {
    if (!isControlled) {
      setUncontrolledOpen(next);
      return;
    }
    onOpenChange?.(typeof next === 'function' ? next(open) : next);
  };
  const [menuStyle, setMenuStyle] = useState(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setMenuStyle({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const close = (event) => {
      if (
        anchorRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    const id = window.setTimeout(() => {
      document.addEventListener('click', close);
    }, 0);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('click', close);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open]);

  const pickSingleDraft = () => {
    setOpen(false);
    onSingleDraftClick?.();
  };

  const pickBulkUpload = () => {
    setOpen(false);
    onBulkUploadClick?.();
  };

  const menu =
    open && menuStyle
      ? createPortal(
          <NewDraftMenu
            ref={menuRef}
            role="menu"
            style={{
              top: menuStyle.top,
              left: menuStyle.left,
              width: menuStyle.width,
            }}
          >
            <NewDraftMenuItem type="button" role="menuitem" onClick={pickSingleDraft}>
              <NewDraftMenuIcon src={plusViolet} alt="" />
              <NewDraftMenuLabel>Single Draft</NewDraftMenuLabel>
            </NewDraftMenuItem>
            <NewDraftMenuItem type="button" role="menuitem" onClick={pickBulkUpload}>
              <NewDraftMenuIcon src={uploadIcon} alt="" />
              <NewDraftMenuLabel>Bulk Upload</NewDraftMenuLabel>
            </NewDraftMenuItem>
          </NewDraftMenu>,
          document.body,
        )
      : null;

  const toggleMenu = () => {
    setOpen((value) => {
      const next = !value;
      if (next) updateMenuPosition();
      return next;
    });
  };

  return (
    <>
      <SplitButtonWrap ref={anchorRef}>
        <SplitButtonContainer>
          <SplitButtonMain
            type="button"
            onClick={pickSingleDraft}
          >
            + New Draft
          </SplitButtonMain>
          <SplitButtonDropdown
            type="button"
            aria-label="Draft options"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={toggleMenu}
          >
            <SplitButtonChevron src={chevronDown} alt="" />
          </SplitButtonDropdown>
        </SplitButtonContainer>
      </SplitButtonWrap>
      {menu}
    </>
  );
};
